import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatCurrencyCompact, formatMonth } from '../../lib/format.js';
import { cn } from '@tailoring-crm/ui';
import { ChartViewToggle, type ChartView } from '../reports/report-chart.js';
import { AnalyticsPanel } from './analytics-panel.js';
import { CompactMoney } from './figures.js';
import type { MonthRow } from './sales-series.js';
import { SALES_TONE, TONE_CLASSES } from './visuals.js';

/**
 * Six months of sales as six bars, the month in progress included.
 *
 * Drawn in SVG with `preserveAspectRatio="none"` so the bars stretch to whatever width the card
 * has — a 340px phone or a third of a desktop — while the text stays in HTML around it, where it
 * keeps its size instead of being squashed with the drawing. Each bar's value is written above
 * it, short, because a phone has no hover to reveal it; the exact figure is in the bar's tooltip
 * and in the table.
 *
 * As with every chart here (see `report-chart.tsx`), the table is always in the DOM — hidden
 * while the chart shows, there for a screen reader and for the "table" switch — and the drawing
 * is `aria-hidden`.
 */

const WIDTH = 600;
const HEIGHT = 160;
/** Each bar's share of its slot; the rest is the gap between months. */
const BAR_SHARE = 0.64;

export function MonthlySalesBars({ months }: { months: readonly MonthRow[] }): ReactElement {
  const { t } = useTranslation();
  const [view, setView] = useState<ChartView>('chart');
  const title = t('dashboard.analytics.monthlySales');

  const max = months.reduce((most, row) => Math.max(most, row.revenuePaise), 0);
  const empty = max === 0;
  const slot = WIDTH / Math.max(months.length, 1);
  const tone = TONE_CLASSES[SALES_TONE];
  const showChart = view === 'chart' && !empty;

  const labelOf = (row: MonthRow) =>
    row.partial
      ? t('dashboard.analytics.monthSoFar', { month: formatMonth(row.month, 'long') })
      : formatMonth(row.month, 'long');

  return (
    <AnalyticsPanel title={title} actions={<ChartViewToggle view={view} onChange={setView} />}>
      {empty ? <p className="text-sm text-muted-foreground">{t('reports.noData')}</p> : null}

      {showChart ? (
        <div aria-hidden="true" className="flex flex-col gap-1">
          <div
            className="grid gap-1 text-center text-[11px] text-muted-foreground tabular-nums"
            style={{ gridTemplateColumns: `repeat(${String(months.length)}, minmax(0, 1fr))` }}
          >
            {months.map((row) => (
              <span key={row.month} className="truncate" title={formatCurrency(row.revenuePaise)}>
                {formatCurrencyCompact(row.revenuePaise)}
              </span>
            ))}
          </div>
          <svg
            viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
            preserveAspectRatio="none"
            className="h-36 w-full"
            data-testid="monthly-bars"
          >
            {months.map((row, index) => {
              // A month with any sales keeps a sliver of bar, so "small" never reads as "none".
              const height =
                row.revenuePaise === 0 ? 0 : Math.max(2, (row.revenuePaise / max) * HEIGHT);
              return (
                <rect
                  key={row.month}
                  data-month={row.month}
                  x={index * slot + (slot * (1 - BAR_SHARE)) / 2}
                  y={HEIGHT - height}
                  width={slot * BAR_SHARE}
                  height={height}
                  className={tone.fill}
                >
                  <title>{`${labelOf(row)}: ${formatCurrencyCompact(row.revenuePaise)}`}</title>
                </rect>
              );
            })}
            <line
              x1="0"
              x2={WIDTH}
              y1={HEIGHT}
              y2={HEIGHT}
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
              className="stroke-border"
            />
          </svg>
          <div
            className="grid gap-1 text-center text-xs text-muted-foreground"
            style={{ gridTemplateColumns: `repeat(${String(months.length)}, minmax(0, 1fr))` }}
          >
            {months.map((row) => (
              <span key={row.month} className={cn(row.partial && 'font-medium text-foreground')}>
                {formatMonth(row.month)}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <table className={cn('w-full text-sm', showChart && 'sr-only')}>
        <caption className="sr-only">{title}</caption>
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th scope="col" className="py-1 font-medium">
              {t('dashboard.analytics.month')}
            </th>
            <th scope="col" className="py-1 text-right font-medium">
              {t('dashboard.analytics.sales')}
            </th>
          </tr>
        </thead>
        <tbody>
          {months.map((row) => (
            <tr key={row.month} className="border-t border-border">
              <th scope="row" className="py-1.5 text-left font-normal">
                {labelOf(row)}
              </th>
              <td className="py-1.5 text-right tabular-nums">
                <CompactMoney paise={row.revenuePaise} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </AnalyticsPanel>
  );
}
